import React, { useEffect, useState, useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { createRoot } from '@wordpress/element';
import { displayErrorMessage, displayLoader, ConnectionContext, getConnectionInfo, getEndpoint } from './utils';
import { getStoredData } from "./utils/data-pulls";
import { LiveBtn } from "./components/liveBtn";
import { SmallMatchBox } from "./components/smallMatchBox";

// import function to register Swiper custom elements
import { register } from 'swiper/element/bundle';
// register Swiper custom elements
register();


const GamesOnNowApp = (props) => {
    const { 
        ...restProps 
    } = props;

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);

    // Connection Information
    const [connectionInfo, setConnectionInfo] = useState(null);

    const [ activeUIDs, setActiveUIDs ] = useState(null);
    const [ matches, setMatches ] = useState(null);
    const [matchSlider, setMatchSlider] = useState(null);
    const sliderRef = useCallback(node => {
        setMatchSlider(node)
    });



    /**
     * Get our connection info and list of active UIDs
     */
    useEffect(() => {

        // Get our connection info
        getConnectionInfo().then(
            (result) => {
                setConnectionInfo(result.data);
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

        // Get active UIDs list
        let args = [
            {
                key: 'activeUIDs'
            }
        ];
        getStoredData(args).then(
            (result) => {

                // Set Active UIDs
                if ( result.data.activeUIDs ) {
                    setActiveUIDs(result.data.activeUIDs);
                }

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, []);


    /**
     * Get live game list, and refresh based on refreshCounter
     */
    useEffect(() => {
        if ( !connectionInfo || !activeUIDs ) return;

        const liveEndpoint = getEndpoint('match_list_live', {
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id
        }, connectionInfo.environment);
        
        fetch(liveEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
          .then(res => res.json())
          .then(
            (result) => {
                const numberToShow = 20;

                /**
                 * API will return results for each province. 
                 * Use the list of competitions entered on the website to filter only to live matches we want to view
                 */
                let validMatches = [];
                result.forEach((match, i) => {
                    const exists = activeUIDs.includes(match.competition.id);
                    if ( exists ) {
                        validMatches.push(match);
                    }
                });
                
                if ( validMatches.length == 0 ) {
                    setMatches(null);
                } else if ( validMatches.length <= numberToShow ) {
                    setMatches(validMatches);
                } else {
                    setMatches(validMatches.slice(0, numberToShow));
                }

                setIsLoaded(true);
              
            },
            (error) => {
              setIsLoaded(true);
              setError(error);
              console.log(error);
            }
          );

    }, [connectionInfo, activeUIDs]);



    // Initialize Swiper Slider if it exists
    useEffect(() => {
        if ( !matchSlider ) return;

        // swiper parameters
        const swiperParams = {
            slidesPerView: 'auto',
            navigation: {
                nextEl: '.acaSliderBtn--next',
                prevEl: '.acaSliderBtn--prev',
              },
            freeMode: true,
            slidesOffsetAfter: 30,
            slidesOffsetBefore: 30,
            spaceBetween: 20,
            breakpoints: {
                768: {
                    slidesOffsetAfter: 50,
                    slidesOffsetBefore: 50,
                }
            },
            on: {
                init() {
                // ...
                },
            },
        };

        Object.assign(matchSlider, swiperParams);
        matchSlider.initialize();

    }, [matchSlider]);



    // Bail if we don't have data yet
    if (!isLoaded ) {
        return displayLoader('acaLoader--main');
    }

    // If there's no matches and we're loaded, show nothing
    if ( isLoaded && !matches ) {
        return;
    }

    return (
        <ConnectionContext.Provider value={connectionInfo}>
            <section className="acaWidget acaWidget--gamesOnNow">

                <div className="acaWidget__header">
                    <div className="acaWidget__headerMain">
                        <h4 className="acaH2 acaWidget__title">
                            Games On Now
                        </h4>
                        <LiveBtn />
                    </div>
                    <div className="acaWidget__headerAside">
                        <div className="acaSliderBtns">
                            <button className="acaSliderBtn acaSliderBtn--prev"></button>
                            <button className="acaSliderBtn acaSliderBtn--next"></button>
                        </div>
                    </div>
                </div>

                <ErrorBoundary fallback={displayErrorMessage()}>
                    <swiper-container
                        init="false"
                        ref={sliderRef}
                    >
                        {
                            matches ? (
                                matches.map((match, i) => (
                                    <swiper-slide key={i}>
                                        <SmallMatchBox data={match} />
                                    </swiper-slide>
                                ) )
                            ) : ''
                        }
                    </swiper-container>
                </ErrorBoundary>

            </section>
        </ConnectionContext.Provider>
    );
}

export default GamesOnNowApp;

// Render Match Page
const appContainer = document.getElementById('gamesOnNowApp');
const root = createRoot(appContainer);
root.render(<GamesOnNowApp />);



